/**
 * @fileoverview Connect Google sign-in to the backend cookie-session boundary.
 * The Google auth-code flow requests Calendar and Drive scopes; `/api/login`
 * (`kc-internal-backend/functions/auth.py`) exchanges the code, stores the encrypted
 * tokens, and sets the session cookie. `kc-internal-frontend/src/AuthContext.js` owns
 * accepted identity and `kc-internal-frontend/src/authRedirect.js` preserves the in-app
 * destination. Sign-in can still lead to onboarding or be refused for an inactive
 * account; navigating after login does not authorize backend operations.
 */

import React, { useContext, useEffect } from 'react';
import api from '../api';
import ToastContext from '../ToastContext';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { AuthContext } from '../AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    clearStoredPostLoginRedirectPath,
    getStoredPostLoginRedirectPath,
    storePostLoginRedirectPath,
} from '../authRedirect';
import kcLogo from '../assets/img/Logo_1749325971610.png';
import AppIcon from './shared/AppIcon';
import './Login.css';

const runtimeConfig = window.__KC_RUNTIME_CONFIG__ || {};
const devGoogleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "586707673353-qks5slhuu2dcs1qqaan12j30cnb34rnf.apps.googleusercontent.com";

let google_client_id = null
if (process.env.NODE_ENV === 'development') {
    google_client_id = devGoogleClientId
} else {
    google_client_id = runtimeConfig.googleClientId || process.env.REACT_APP_GOOGLE_CLIENT_ID || "799562548601-s6h4sc8cij4j8bpr53nb1la4co8aqp0v.apps.googleusercontent.com" // Cyclone set this one up
}



function Goog() {
    const showToast = useContext(ToastContext);
    const { applyAuthState } = useContext(AuthContext);

    const login = useGoogleLogin({
        onSuccess:

        async (response) => {
            try {
                const loginResponse = await api.post('/login', { code: response.code }, { withCredentials: true })
                if (loginResponse.data.message === 'User logged in') {
                    showToast({ severity: 'success', summary: 'Successful', detail: 'Welcome in' });
                    applyAuthState(loginResponse.data)
                    return
                }

                showToast({ severity: 'error', summary: 'Error', detail: 'Unable to sign in' });
            } catch (error) {
                console.error(error);
                showToast({ severity: 'error', summary: 'Error', detail: error.toString() });
            }
        },
        onError: (error) => {
            console.error(error);
        },
        flow: 'auth-code',
        scope: 'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/drive'
    });

    return (
        <button onClick={login} className="login-google-btn">
            <AppIcon icon="brand-google" size={20} />
            Sign in with Google
        </button>
    );
}



function Login() {
    const { isAuthenticated, requiresOnboarding } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        document.title = "Login"
        storePostLoginRedirectPath(location.state?.from, localStorage.getItem('postLoginRedirectPath'))
    }, [location.state]);

    useEffect(

    () => {
        if (!isAuthenticated) {
            return
        }

        if (requiresOnboarding) {
            navigate('/onboarding', { replace: true })
            return
        }

        const redirectPath = getStoredPostLoginRedirectPath()
        clearStoredPostLoginRedirectPath()
        navigate(redirectPath, { replace: true })
    }, [isAuthenticated, navigate, requiresOnboarding]);

    return (
        <GoogleOAuthProvider clientId={google_client_id}>
            <div className="login-page">
                <div className="login-card">
                    <img src={kcLogo} alt="Kneupper & Covey" className="login-logo" />
                    <h1 className="login-heading">Welcome back</h1>
                    <p className="login-subtext">Sign in to continue to your dashboard</p>
                    <Goog />
                    <div className="login-footer">
                        Kneupper & Covey, PC
                    </div>
                </div>
            </div>
        </GoogleOAuthProvider>
    );
}

export default Login;
